import React, { forwardRef, useState } from "react";
import { Button, Chip, Grid, Tooltip } from "@mui/material";
import { Edit as EditIcon, PhoneDisabledOutlined } from "@mui/icons-material";

import GridListItem from "components/common/grid/GridListItem";
import PhoneDisplay from "components/common/phone/PhoneDisplay";
import usePatientInformation from "components/pages/transmissions/modal/patient/tabs/generalInformation/patientInformation/usePatientInformation";
import PatientInformationForm from "components/pages/transmissions/modal/patient/tabs/generalInformation/patientInformation/PatientInformationForm";

import { showToast } from "utils/toast";
import { getFormattedDate } from "utils/date";

const PatientInformation = forwardRef((props, ref) => {
  const {
    newPatientInformation,
    handleInformation,
    handleCancel,
    phoneOptOutInformation,
  } = usePatientInformation();

  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const renderPatientInfo = (information) => {
    const {
      phone1,
      phone2,
      email,
      street1,
      street2,
      city,
      state,
      zip,
      emergency_contact,
    } = information;

    return (
      <Grid container columnSpacing={3} className="view-pdf__drawer-grid">
        <Grid item container md={4} alignItems="center" spacing={0.5}>
          <GridListItem
            title="PHONE #1"
            subTitle={
              <PhoneDisplay
                phone={phone1}
                isOptOut={phoneOptOutInformation?.primaryNumberOptOut}
              />
            }
            size="auto"
            copyValue={phone1}
          />

          {phoneOptOutInformation?.primaryNumberOptOut && (
            <Grid item xs="auto" display="flex" alignItems="center">
              <Tooltip
                title={`Primary number opted out on ${getFormattedDate(phoneOptOutInformation.primaryNumberOptOut.optOutDate, "MM/DD/YYYY, hh:mm a")}`}
              >
                <Chip
                  icon={<PhoneDisabledOutlined />}
                  color="error"
                  label="Opt Out"
                />
              </Tooltip>
            </Grid>
          )}
        </Grid>

        <Grid item container md={4} alignItems="center" spacing={0.5}>
          <GridListItem
            title="PHONE #2"
            subTitle={
              <PhoneDisplay
                phone={phone2}
                isOptOut={phoneOptOutInformation?.secondaryNumberOptOut}
              />
            }
            size="auto"
            copyValue={phone2}
          />

          {phoneOptOutInformation?.secondaryNumberOptOut && (
            <Grid item xs="auto" display="flex" alignItems="center">
              <Tooltip
                title={`Secondary number opted out on ${getFormattedDate(phoneOptOutInformation.secondaryNumberOptOut.optOutDate, "MM/DD/YYYY, hh:mm a")}`}
              >
                <Chip
                  icon={<PhoneDisabledOutlined />}
                  color="error"
                  label="Opt Out"
                />
              </Tooltip>
            </Grid>
          )}
        </Grid>

        <GridListItem
          title="EMAIL"
          subTitle={email ?? "—"}
          size="4"
          copyValue={email}
        />

        <Grid item container md={4} alignItems="center" spacing={0.5}>
          <GridListItem
            title="EMERGENCY CONTACT PHONE"
            subTitle={
              <PhoneDisplay
                phone={emergency_contact?.phone}
                isOptOut={phoneOptOutInformation?.emergencyPhoneOptOut}
              />
            }
            size="auto"
            copyValue={emergency_contact?.phone}
          />

          {phoneOptOutInformation?.emergencyPhoneOptOut && (
            <Grid item xs="auto" display="flex" alignItems="center">
              <Tooltip
                title={`Emergency contact number opted out on ${getFormattedDate(phoneOptOutInformation.emergencyPhoneOptOut.optOutDate, "MM/DD/YYYY, hh:mm a")}`}
              >
                <Chip
                  icon={<PhoneDisabledOutlined />}
                  color="error"
                  label="Opt Out"
                />
              </Tooltip>
            </Grid>
          )}
        </Grid>

        <GridListItem
          title="EMERGENCY CONTACT NAME"
          subTitle={emergency_contact?.name ?? "—"}
          size="4"
        />
        <GridListItem
          title="EMERGENCY CONTACT RELATIONSHIP"
          subTitle={emergency_contact?.relation ?? "—"}
          size="4"
        />
        <GridListItem
          title="STREET ADDRESS #1"
          subTitle={street1 ?? "—"}
          size="4"
        />
        <GridListItem
          title="STREET ADDRESS #2"
          subTitle={street2 ?? "—"}
          size="4"
        />
        <GridListItem title="CITY" subTitle={city ?? "—"} size="4" />
        <GridListItem title="STATE" subTitle={state ?? "—"} size="4" />
        <GridListItem title="ZIP" subTitle={zip ?? "—"} size="4" />
      </Grid>
    );
  };

  const handlePatientInformationSave = async (values) => {
    try {
      setIsLoading(true);
      const response = await handleInformation(values);
      if (!response.error) {
        setIsEditMode(false);
      }
    } catch (error) {
      showToast({ message: error.message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="patient-modal__section" ref={ref}>
      <div className="patient-form__row patient-form__info">
        <div className="mb-3">
          <Grid container>
            <Grid item xs={11} style={{ textAlign: "left" }}>
              <div className="patient-form__info--header">
                Contact Information
              </div>
            </Grid>
            {!isEditMode && (
              <Grid item xs={1} style={{ textAlign: "right" }}>
                <Button
                  variant="text"
                  color="secondary"
                  className="profile-edit-button"
                  onClick={() => setIsEditMode(true)}
                >
                  <EditIcon
                    className="patient-edit-icon billing-cycle-edit-icon"
                    data-action="edit-contact-information-icon"
                  />
                  Edit
                </Button>
              </Grid>
            )}
          </Grid>
          {isEditMode ? (
            <PatientInformationForm
              handleSubmit={handlePatientInformationSave}
              isLoading={isLoading}
              handleCancel={handleCancel}
              setIsEditMode={setIsEditMode}
            />
          ) : (
            renderPatientInfo(newPatientInformation)
          )}
        </div>
      </div>
    </div>
  );
});

export default PatientInformation;
